'use client'
import * as React from 'react'
import { clsx } from 'clsx'
import { Resizable, ResizeCallback } from 're-resizable'
import { useStorage } from '~/hooks/use-storage'
import { /*ArrowRightIcon,*/ HamburgerMenuIcon } from '@radix-ui/react-icons'
type CloseCallback = (isToggled: boolean) => void | Promise<void>

const SIZE_TO_CLOSE = 20
const SIZE_TO_OPEN = 100

export const ResizableContainer = ({
  children,
  minWidth,
  maxWidth,
  defaultWidth,
  className,
  direction = 'right',
  noBorderHighlight,
  containerClassName,
  closedWidth = 10,
  widthLocalStorageKey,
  setIsClosed,
  isClosed,
  sizeToOpen = SIZE_TO_OPEN,
  sizeToClose = SIZE_TO_CLOSE,
}: {
  children?: React.ReactNode
  minWidth?: number
  maxWidth?: number
  defaultWidth: number
  className?: string
  widthLocalStorageKey?: string
  direction?: 'left' | 'right'
  noBorderHighlight?: boolean
  containerClassName?: string
  closedWidth?: number
  setIsClosed?: CloseCallback
  isClosed?: boolean
  sizeToOpen?: number
  sizeToClose?: number
}) => {
  const [isResizing, setIsResizing] = React.useState(false)

  // an auxiliar local state to handle the close state of the sidebar when not provide by props
  const [close, setClose] = React.useState(isClosed)
  const resolvedIsClosed = isClosed ?? close

  const [width, setWidth] = useStorage(
    'localStorage',
    (z) => z.number(),
    widthLocalStorageKey ?? 'sidebarWidth',
    { initialValue: defaultWidth }
  )
  const [wasMoved, setWasMoved] = React.useState(false)

  const handleResize = React.useCallback<ResizeCallback>(
    (e, _dir, el, _delta) => {
      const clientX =
        e instanceof MouseEvent ? e.clientX : e.touches.item(0)?.clientX

      el.style.setProperty('--resizer-width', `${el.offsetWidth}px`)

      // if the sidebar is closed, we only want to open it if the user drags it
      if (_delta.width !== 0 && !wasMoved) setWasMoved(true)
      if (isClosed ?? close) {
        if (
          clientX &&
          (direction === 'right'
            ? clientX > sizeToOpen
            : clientX < window.innerWidth - sizeToOpen)
        ) {
          setClose(false)
          setIsClosed?.(false)
        }
        return
      }
      // if the sidebar is open, we only want to close it if the user drags it
      if (
        clientX &&
        (direction === 'right'
          ? clientX < sizeToClose // case dir 'left' -> sidebar of the left
          : clientX > window.innerWidth - sizeToClose) // case dir 'right' -> sidebar of the right
      ) {
        setIsClosed?.(true)
        setClose(true)
      }
    },
    [close, direction, isClosed, setIsClosed, sizeToClose, sizeToOpen, wasMoved]
  )

  return (
    <div
      className={clsx('relative', containerClassName)}
      id={widthLocalStorageKey}
    >
      {resolvedIsClosed && (
        <button
          onClick={() => {
            setClose(false)
            setIsClosed?.(false)
          }}
          className="pointer-events-none absolute top-1/2 z-50 flex h-8 w-8 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full border border-dark-border-solid bg-dark-bg hover:bg-dark-shade"
          style={{
            width: 12,
            height: 24,
            left: direction === 'left' ? 0 : undefined,
            right: direction === 'right' ? 0 : undefined,
            transform: `translate(${
              direction === 'left' ? '-50%' : '50%'
            },-100%)`,
          }}
        >
          <HamburgerMenuIcon className="h-6 w-4 text-dark-muted" />
        </button>
      )}
      <Resizable
        onResizeStart={() => {
          setIsResizing(true)
        }}
        onResize={handleResize}
        onResizeStop={(_event, _direction, elementRef, d) => {
          const isOnClick = d.width === 0 && d.height === 0
          if (!wasMoved && isOnClick) {
            setClose(!close)
            setIsClosed?.(!isClosed)
          } else {
            const newWidth = elementRef.getBoundingClientRect().width
            setWidth(newWidth)
            setIsResizing(false)
          }
          setWasMoved(false)
        }}
        minWidth={resolvedIsClosed ? closedWidth : minWidth}
        maxWidth={resolvedIsClosed ? closedWidth : maxWidth}
        enable={{ left: direction === 'left', right: direction === 'right' }}
        size={{
          width: width ?? 0,
          height: '100%',
        }}
        className={clsx(
          className,
          'relative flex-shrink-0 overflow-visible',
          resolvedIsClosed && '[&_>_*:not(.resizer)]:hidden'
        )}
        handleClasses={{
          [direction]: clsx(
            'after:absolute z-40 after:h-full after:opacity-0 after:transition-opacity after:w-[3px] relative after:-translate-x-1/2 after:left-1/2 after:bg-dark-control-base',
            !noBorderHighlight && 'hover:after:opacity-30',
            !noBorderHighlight && isResizing && 'after:opacity-30',
            !noBorderHighlight && close && 'max-w-8'
          ),
        }}
        style={{
          ['--resizer-width' as string]: `${width}px`,
        }}
        handleWrapperClass="resizer"
      >
        {children}
      </Resizable>
    </div>
  )
}
